import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import SettingsPageLayout from './SettingsPageLayout';
import { useFinishSetup } from '../onboarding/finishSetup';
import { SETUP_V2_ENABLED } from '../onboarding/flags';
import { useSetup, openSetup } from '../onboarding/setup/SetupContext';
import { useSetupFeatured } from '../onboarding/setup/SetupHero';
import { heldCharges, minutesLeftLabel } from '../onboarding/setup/setupPlan';
import { ImportHero } from './ImportData';
import './FinishSetupSettings.css';

/**
 * Finish setting up (ONBOARDING-FIRST-RUN-PLAN.md, Step G).
 *
 * The jobs that were deliberately kept out of the first ten minutes. Rows
 * tick themselves from the account, so this stays honest whether the owner
 * came through setup or did everything by hand.
 *
 * With Set up with Bailey, anyone who has a plan gets its card here: where
 * they are, the chapters still open (including any saved for later, which
 * stay reachable after going live), and the deposits or charges held back
 * until card payments are live, each one tap from where it's switched on.
 * Once the plan is done the list worked out from the account shows too.
 */

const Tick = () => (
  <svg width="12" height="12" viewBox="0 0 16 16" fill="none"><path d="M3.5 8.5l3 3 6-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
);

function BaileyPlanCard({ setup }) {
  const { chapters, statusOf, doneCount, total, minutesLeft, state, role } = setup;
  const finished = state.status === 'done';
  const started = state.status !== 'new' || doneCount > 0;
  const open = chapters.filter((c) => statusOf(c.id) !== 'done');
  const later = open.filter((c) => statusOf(c.id) === 'skipped').length;
  const pct = total ? Math.round((doneCount / total) * 100) : 0;
  const answers = state.answers || {};
  const held = role === 'owner' ? heldCharges(answers) : { any: false };
  const heldWhat = [held.deposits ? 'deposits' : '', held.policy ? 'cancellation policy' : ''].filter(Boolean).join(' and ');

  const openChapter = (id) => setup.start({ chapter: id });
  const openHeld = () => {
    setup.setStep('paid', { ...((state.steps || {}).paid || {}), id: 'held' });
    setup.start({ chapter: 'paid' });
  };

  let detail;
  if (finished) {
    detail = open.length
      ? `You’re set up. ${open.length === 1 ? 'One step is' : `${open.length} steps are`} still open whenever you want ${open.length === 1 ? 'it' : 'them'}.`
      : 'You’re set up. Open it any time to check or change a step.';
  } else {
    detail = `${doneCount} of ${total} done${later ? `, ${later} saved for later` : ''}. ${minutesLeftLabel(minutesLeft)}.`;
  }

  return (
    <div className="settings-card">
      <div className="settings-card-header fs-head">
        <div>
          <h2>Set up with Bailey</h2>
          <p>{detail}</p>
        </div>
        <span className={`settings-badge ${finished ? 'settings-badge-info' : 'settings-badge-default'}`}>{doneCount} of {total} done</span>
      </div>
      {!finished && <div className="fs-bar" aria-hidden="true"><i style={{ width: `${pct}%` }} /></div>}

      <ul className="fs-list">
        {held.any && (
          <li className="fs-item">
            <span className="fs-tick" aria-hidden="true" />
            <div className="fs-text">
              <b>Your {heldWhat}</b>
              <span>Held back until card payments are live, so no client pays anything yet. Switch {held.deposits && held.policy ? 'them' : 'it'} on here once they are.</span>
            </div>
            <button type="button" className="fs-go" onClick={openHeld}>Open</button>
          </li>
        )}
        {chapters.map((c) => {
          const st = statusOf(c.id);
          if (finished && st === 'done') return null;
          return (
            <li key={c.id} className={`fs-item${st === 'done' ? ' is-done' : ''}`}>
              <span className={`fs-tick${st === 'done' ? ' is-on' : ''}`} aria-hidden="true">{st === 'done' ? <Tick /> : null}</span>
              <div className="fs-text">
                <b>{c.long}</b>
                <span>{st === 'done' ? 'Done' : st === 'skipped' ? 'Saved for later' : c.minutes ? `About ${c.minutes} min` : ''}</span>
              </div>
              <button type="button" className="fs-go" onClick={() => openChapter(c.id)}>{st === 'done' ? 'Review' : 'Open'}</button>
            </li>
          );
        })}
      </ul>

      <div className="fs-actions fs-actions--plan">
        {!finished && <span className="fs-left">{minutesLeftLabel(minutesLeft)}</span>}
        <button type="button" className="settings-btn settings-btn-primary" onClick={() => setup.start({})}>
          {finished ? 'Review with Bailey' : started ? 'Continue with Bailey' : 'Start with Bailey'}
        </button>
      </div>
    </div>
  );
}

export default function FinishSetupSettings(props) {
  const history = useHistory();
  const setup = useSetup();
  const plan = SETUP_V2_ENABLED && setup && setup.enabled && setup.ready && setup.total > 0 ? setup : null;
  const status = plan ? plan.state.status : null;
  // A plan they've started always has its card here, before and after going
  // live. One not started yet only when it's offered: a salon that went live
  // before Bailey's setup existed, or an established one, keeps the list
  // worked out from its account rather than "0 of 10" and a second go-live.
  const started = !!plan && (status !== 'new' || plan.settledCount > 0);
  const planCard = !!plan && (started || plan.offered);
  const showList = !planCard || status === 'done';
  // On a refresh or a link straight here the plan isn't in yet, and it may
  // be about to send them into setup: hold the old list back for a moment
  // rather than show it and jump. Never for long, so a failed read still
  // leaves them a page.
  const waiting = SETUP_V2_ENABLED && !!setup && setup.enabled && !setup.ready;
  const [gaveUp, setGaveUp] = useState(false);
  useEffect(() => {
    if (!waiting) return undefined;
    const t = setTimeout(() => setGaveUp(true), 3000);
    return () => clearTimeout(t);
  }, [waiting]);
  const hold = waiting && !gaveUp;
  const { items, doneCount, countable } = useFinishSetup(showList && !hold);
  const allDone = countable > 0 && doneCount === countable;

  // While setup is unfinished, Set up with Bailey is the one place for it
  // (as the settings hub already does): this page would only repeat its
  // list with a button into it. Reached some other way (the tab strip,
  // search, a link), open the flow itself over Settings instead.
  const setupFeatured = useSetupFeatured();
  const sent = useRef(false);
  useEffect(() => {
    if (!setupFeatured || sent.current) return;
    sent.current = true;
    history.replace('/settings');
    openSetup({});
  }, [setupFeatured, history]);
  if (setupFeatured) return null;
  if (hold) return <SettingsPageLayout activeTab="finish-setup" {...props}><div className="catalogue-content-wrapper fs-page" /></SettingsPageLayout>;

  return (
    <SettingsPageLayout activeTab="finish-setup" {...props}>
      <div className="catalogue-content-wrapper fs-page">
        {planCard && <BaileyPlanCard setup={plan} />}
        {showList && (
        <div className="settings-card">
          <div className="settings-card-header fs-head">
            <div>
              <h2>Finish setting up</h2>
              <p>{allDone
                ? 'The important things are done. The rest is worth a look when you have a minute.'
                : 'None of this stops you taking bookings. It is the tidying up that makes the rest of Beu work harder.'}</p>
            </div>
            {countable > 0 && (
              <span className={`settings-badge ${allDone ? 'settings-badge-info' : 'settings-badge-default'}`}>
                {doneCount} of {countable} done
              </span>
            )}
          </div>

          <ul className="fs-list">
            {items.map((item) => (
              <li key={item.key} className={`fs-item${item.done ? ' is-done' : ''}`}>
                <span className={`fs-tick${item.done ? ' is-on' : ''}`} aria-hidden="true">
                  {item.done ? <Tick /> : null}
                </span>
                <div className="fs-text">
                  <b>{item.title}</b>
                  <span>{item.blurb}</span>
                </div>
                <button type="button" className="fs-go" onClick={() => history.push(item.path)}>
                  {item.done ? 'Review' : item.cta}
                </button>
              </li>
            ))}
          </ul>
        </div>
        )}

        {/* The eye-catching import card. */}
        <ImportHero />
      </div>
    </SettingsPageLayout>
  );
}
